import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Wrench } from "lucide-react";

const PESO = "\u20B1";
const MEDICAL_CATEGORIES = ["Consultation", "Certificate", "Equipment", "Laboratory", "Procedure", "Nursing", "Other"];
const DENTAL_CATEGORIES = [
  "Dental - Diagnostic", "Dental - Preventive", "Dental - Restorative",
  "Dental - Endodontic", "Dental - Periodontic", "Dental - Surgical",
  "Dental - Prosthodontic", "Dental - Orthodontic", "Dental - Cosmetic",
];

// The backend's category whitelist is global, so a medical clinic would
// otherwise be offered nine dental categories it will never use. A dental clinic
// still gets the generic ones — it sells certificates and consultations too.
function categoriesFor(clinicType) {
  return clinicType === "dental"
    ? [...DENTAL_CATEGORIES, ...MEDICAL_CATEGORIES]
    : MEDICAL_CATEGORIES;
}

const EMPTY = {
  name: "", category: "Other", default_price: 0,
  shortcut_enabled: false, is_consultation: false, generates_certificate: false,
};

export default function POSServices() {
  const { user } = useAuth();
  const CATEGORIES = categoriesFor(user?.clinic_type);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "create", form: { ...EMPTY }, id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pos/services");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => setDialog({ open: true, mode: "create", form: { ...EMPTY }, id: "" });
  const openEdit = (it) => setDialog({ open: true, mode: "edit", id: it.id, form: {
    name: it.name, category: it.category, default_price: it.default_price,
    shortcut_enabled: !!it.shortcut_enabled, is_consultation: !!it.is_consultation, generates_certificate: !!it.generates_certificate,
  }});

  const submit = async () => {
    const f = dialog.form;
    if (!f.name) { toast.error("Name is required"); return; }
    try {
      if (dialog.mode === "create") {
        await api.post("/pos/services", { ...f, default_price: Number(f.default_price) || 0 });
        toast.success("Service created");
      } else {
        await api.put(`/pos/services/${dialog.id}`, { ...f, default_price: Number(f.default_price) || 0 });
        toast.success("Service updated");
      }
      setDialog({ ...dialog, open: false });
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Deactivate "${it.name}"? It will no longer appear in POS.`)) return;
    try {
      await api.delete(`/pos/services/${it.id}`);
      toast.success("Service removed");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  return (
    <div className="p-5 lg:p-7 space-y-5" data-testid="pos-services-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Wrench size={20} className="text-blue-600" /> POS Services</h1>
          <p className="text-sm text-slate-600">Manage the catalog of billable services for your hospital.</p>
        </div>
        <Button onClick={openCreate} data-testid="btn-new-service"><Plus size={14} className="mr-1" /> New Service</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="services-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Category</th>
              <th className="text-right px-4 py-2 font-medium">Default Price</th>
              <th className="text-left px-4 py-2 font-medium">Tags</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={5} className="text-center text-slate-500 py-8">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-slate-500 py-8">No services yet. Create one.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`svc-row-${it.id}`}>
                <td className="px-4 py-2 font-medium">{it.name}</td>
                <td className="px-4 py-2"><Badge variant="outline">{it.category}</Badge></td>
                <td className="px-4 py-2 text-right">{PESO}{Number(it.default_price).toFixed(2)}</td>
                <td className="px-4 py-2">
                  <div className="flex gap-1 flex-wrap">
                    {it.shortcut_enabled ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Shortcut</Badge> : null}
                    {it.is_consultation ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Consultation</Badge> : null}
                    {it.generates_certificate ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Certificate</Badge> : null}
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`svc-edit-${it.id}`}><Edit2 size={12} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it)} data-testid={`svc-del-${it.id}`}><Trash2 size={12} className="text-rose-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({...dialog, open: o})}>
        <DialogContent data-testid="svc-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.mode === "create" ? "New Service" : "Edit Service"}</DialogTitle>
            <DialogDescription>Configure the service appearing in POS.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={dialog.form.name} onChange={(e) => setDialog({...dialog, form: {...dialog.form, name: e.target.value}})} data-testid="svc-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={dialog.form.category} onValueChange={(v) => setDialog({...dialog, form: {...dialog.form, category: v}})}>
                  <SelectTrigger data-testid="svc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`svc-cat-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Price (₱)</Label>
                <Input type="number" min="0" step="0.01" value={dialog.form.default_price}
                  onChange={(e) => setDialog({...dialog, form: {...dialog.form, default_price: e.target.value}})}
                  data-testid="svc-price" />
              </div>
            </div>
            <div className="flex items-center gap-2"><Checkbox checked={dialog.form.shortcut_enabled} onCheckedChange={(v) => setDialog({...dialog, form: {...dialog.form, shortcut_enabled: !!v}})} id="svc-sc" data-testid="svc-shortcut" /><label htmlFor="svc-sc" className="text-sm">Show as quick-add shortcut in POS</label></div>
            <div className="flex items-center gap-2"><Checkbox checked={dialog.form.is_consultation} onCheckedChange={(v) => setDialog({...dialog, form: {...dialog.form, is_consultation: !!v}})} id="svc-cons" data-testid="svc-consultation" /><label htmlFor="svc-cons" className="text-sm">Use as default Consultation Fee</label></div>
            <div className="flex items-center gap-2"><Checkbox checked={dialog.form.generates_certificate} onCheckedChange={(v) => setDialog({...dialog, form: {...dialog.form, generates_certificate: !!v}})} id="svc-cert" data-testid="svc-certificate" /><label htmlFor="svc-cert" className="text-sm">Generates a Medical Certificate document</label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({...dialog, open: false})}>Cancel</Button>
            <Button onClick={submit} data-testid="btn-svc-submit">{dialog.mode === "create" ? "Create" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
